import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, X, ArrowRight, FileSpreadsheet } from 'lucide-react';

const STORAGE_KEY = 'pfd_banner_direct_upload_dismissed_v1';

/**
 * NewFeatureBanner — dismissible announcement for the new Direct Upload
 * capability (AquaManager + Fishtalk raw Excel exports uploaded natively,
 * no template conversion required).
 *
 * Dismissal is persisted in localStorage so it stays hidden per browser.
 * We intentionally don't back this with a per-user DB flag — a browser-
 * scoped dismissal is the right granularity for a "new feature" banner
 * (users testing on multiple devices will re-see it, which is desirable).
 */
export default function NewFeatureBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      setVisible(localStorage.getItem(STORAGE_KEY) !== '1');
    } catch { setVisible(true); }
  }, []);

  const dismiss = (e) => {
    e?.stopPropagation?.();
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  const open = () => navigate('/spreadsheet-comparison');

  if (!visible) return null;

  return (
    <Card
      className="relative overflow-hidden border-2 border-cyan-400 bg-gradient-to-br from-cyan-50 via-white to-blue-50 dark:from-cyan-900/30 dark:via-slate-900 dark:to-blue-900/30 shadow-md cursor-pointer hover:shadow-lg transition-all"
      onClick={open}
      data-testid="new-feature-banner"
    >
      <div className="absolute -top-10 -right-10 w-48 h-48 bg-cyan-200/30 dark:bg-cyan-500/10 rounded-full pointer-events-none" />

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 p-1 rounded-full text-slate-500 hover:text-slate-900 hover:bg-white/70 dark:hover:bg-slate-800 z-10 transition-colors"
        data-testid="new-feature-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="p-4 sm:p-5 flex items-start sm:items-center gap-4 flex-col sm:flex-row">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-md">
            <FileSpreadsheet className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-600 text-white">
                <Sparkles className="w-3 h-3" />
                New feature
              </span>
              <h3 className="text-base font-bold text-cyan-800 dark:text-cyan-200">
                Direct AquaManager &amp; Fishtalk Excel upload
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 mt-1 leading-snug">
              Skip the manual template conversion &mdash; upload your raw
              AquaManager or Fishtalk Excel exports directly. Multi-cage
              batches, mixed-date rescue and cycle-boundary detection all
              handled automatically.
            </p>
          </div>
        </div>
        <Button
          onClick={(e) => { e.stopPropagation(); open(); }}
          className="bg-cyan-600 hover:bg-cyan-700 text-white shadow self-stretch sm:self-auto flex-shrink-0"
          data-testid="new-feature-banner-cta"
        >
          Try it now
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </Card>
  );
}
